import { clsx } from "clsx";
import React, { useRef } from "react";
import AppFooter from "web/react/components/app-footer/app-footer";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { Divider } from "web/react/components/divider";
import { FooterProps } from "web/react/components/footer/footer";
import { InfoCallout } from "web/react/emo/info-callout";
import { gettext } from "web/script/modules/django-i18n";
import {
    ProductBuyOptionsSerializer,
    ProductLayoutSerializer,
    ProductPageAreaSerializer,
} from "web/types/serializers";
import * as styles from "./out-of-stock-product-page.css";
import { ProductArea } from "./product-area";

const getInfoCalloutTitle = (
    buyOptions: ProductBuyOptionsSerializer[],
    uniqueRetailerCount: number,
    isCheckout: boolean
): string => {
    if (!buyOptions?.length) return gettext("oos_product.info_callout_no_retailers");
    else if (isCheckout) return gettext("oos_product.info_callout_icon");
    else {
        return uniqueRetailerCount === 2
            ? gettext("oos_product.info_callout_affiliate_two_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
              })
            : uniqueRetailerCount > 2
            ? gettext("oos_product.info_callout_affiliate_multiple_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
                  retailer_count: uniqueRetailerCount - 1,
              })
            : gettext("oos_product.info_callout_affiliate", {
                  retailer_name: buyOptions[0].retailer_name,
              });
    }
};

// remove me as the page is built
const TempSection = ({
    title,
    style,
}: {
    title: string;
    style?: React.CSSProperties;
}): React.ReactElement => {
    return (
        <div className={styles.tempSectionContainer} style={style}>
            {title}
        </div>
    );
};

function SectionDivider({ isMobileOnly }: { isMobileOnly?: boolean }): React.ReactElement {
    return (
        <div className={clsx({ [styles.hideDesktop]: isMobileOnly })}>
            <Divider />
        </div>
    );
}

export interface OutOfStockProductPageProps {
    productArea: ProductPageAreaSerializer;
    productLayout: ProductLayoutSerializer;
    mainBuyOption: ProductBuyOptionsSerializer;
    isCheckout: boolean;
    isOverlay?: boolean;
    parentRef?: React.RefObject<HTMLElement>;
    footerData?: FooterProps;
}
export function OutOfStockProductPage({
    productArea,
    // productLayout,
    isOverlay,
    isCheckout,
    mainBuyOption,
    // parentRef,
    footerData,
}: OutOfStockProductPageProps): React.ReactElement {
    const uniqueRetailerCount = productArea.unique_retailers.length;
    const productDetailsContainerRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <div className={styles.pageWrapper}>
                {!isOverlay && <CustomerCaptureOverlay />}
                <div
                    className={clsx(
                        styles.pageContent,
                        styles.pageContentPadding[isOverlay ? "overlay" : "standard"]
                    )}
                >
                    <div className={styles.heroSection}>
                        <InfoCallout
                            variant="information"
                            title={getInfoCalloutTitle(
                                productArea?.buy_options,
                                uniqueRetailerCount,
                                isCheckout
                            )}
                        />
                        <TempSection title={"last seen"} />
                        <div className={styles.mainBuyOptionPlusOtherOptionsContainer}>
                            <ProductArea
                                productArea={productArea}
                                uniqueRetailerCount={uniqueRetailerCount}
                                isCheckout={isCheckout}
                                mainBuyOption={mainBuyOption}
                                productDetailsContainerRef={productDetailsContainerRef}
                            />
                            <TempSection
                                style={{ width: "427px" }}
                                title={"previously available"}
                            />
                        </div>
                    </div>
                    <SectionDivider />
                    <TempSection title={"pba"} />
                    <TempSection title={"more like this grid"} />
                    <TempSection title={"similar brands to"} />
                    <SectionDivider isMobileOnly />
                    <div ref={productDetailsContainerRef} id="product-details">
                        <TempSection title={"full product details"} />
                    </div>
                    <TempSection title={"people also searched for"} />
                    <SectionDivider isMobileOnly />
                    <TempSection title={"recently viewed"} />
                </div>
            </div>
            {footerData && (
                <AppFooter
                    data={footerData.data}
                    helpCentreURLs={footerData.helpCentreURLs}
                    country={footerData.country}
                />
            )}
        </>
    );
}
